import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useWindowStorage from 'components/hooks/useWindowStorage';
import useWindowStorageRemove from 'components/hooks/useWindowStorageRemove';

import Tabs from 'components/application/Tabs';

import BookingsTab from 'components/bookings/BookingsTab';
import { useCurrentActor } from 'components/contexts/CurrentActor';
import { TrainingRegisterResourceManagementContext } from 'components/contexts/TrainingRegisterResourceManagementContext';
import CoursesTab from 'components/courses/CoursesTab';
import PersonnelTab from 'components/personnel/PersonnelTab';
import RolesTab from 'components/roles/RolesTab';
import SubcontractorsTab from 'components/subcontractors/SubcontractorsTab';
import TeamsTab from 'components/teams/TeamsTab';
import TrainingRegisterReportsTab from 'components/trainingRegisterReports/TrainingRegisterReportsTab';

const trainingRegisterTabs = [
  'personnel',
  'teams',
  'roles',
  'courseRegister',
  'bookings',
  'subcontractors',
  'reports',
];

const trainingRegisterStorageKeys = [
  'trainingRegister|initialActiveTab',
  ...trainingRegisterTabs.map((tab) => `trainingRegister|${tab}`),
];

export default function PersonnelIndexView({
  emailFilter,
  hasArchivedPersonnelAccess,
  hasBookingGroupEditableAccess,
  hasBookingGroupViewableAccess,
  hasCompanyRolesViewableAccess,
  hasCourseEditableAccess,
  hasCourseViewableAccess,
  hasPersonnelEditableAccess,
  hasPersonnelViewableAccess,
  hasProfileAccessEditableAccess,
  hasRoleEditableAccess,
  hasSubcontractorPersonnelViewableAccess,
  hasSubcontractorViewableAccess,
  hasSubordinateManagementFeatureAccess,
  hasTRPersonnelAccess,
  // not TR within context
  hasTeamDashboardAccess,
  hasTeamEditableAccess,
  hasTeamPersonnelEditableAccess,
  hasTeamViewableAccess,
  hasTrainingWriteAccess,
  initialTrainingFilters,
  isAutoEnrolmentIncludingMissingTraining,
  isCourseExpiryDateEditable,
  isMultiDivisionAccount,
  managerAccessTypes,
}) {
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [getCurrentDivision, setCurrentDivision] = useWindowStorage(
    `trainingRegister|currentDivision`,
    { store: window.sessionStorage },
  );

  useWindowStorageRemove(
    trainingRegisterStorageKeys,
    () => {
      if (
        performance.getEntriesByType('navigation')[0].type === 'reload' ||
        !document.referrer
      )
        return false;

      try {
        const referrerUrl = new URL(document.referrer);
        return (
          currentActor.division.id !== getCurrentDivision() ||
          !referrerUrl.pathname.startsWith('/personnel')
        );
      } catch {
        return false;
      }
    },
    { store: window.sessionStorage },
  );

  setCurrentDivision(currentActor.division.id);

  const searchParams = new URLSearchParams(window.location.search);

  const searchParamsTab =
    trainingRegisterTabs.includes(searchParams.get('tab')) ?
      searchParams.get('tab')
    : null;

  const [getInitialActiveTabStore, setInitialActiveTabStore] = useWindowStorage(
    `trainingRegister|initialActiveTab`,
    { store: window.sessionStorage },
  );

  const initialActiveTab = searchParamsTab || getInitialActiveTabStore();

  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const hasTeamManagerAccess = managerAccessTypes.includes('teamManager');
  const hasLineManagerAccess = managerAccessTypes.includes('lineManager');

  const isReportsTabViewable =
    currentActor.isAllowedFeature('training_register') &&
    hasPersonnelViewableAccess;

  const trainingRegisterContextValues = {
    hasArchivedPersonnelAccess,
    hasBookingGroupEditableAccess,
    hasBookingGroupViewableAccess,
    hasCourseEditableAccess,
    hasLineManagerAccess,
    hasPersonnelEditableAccess,
    hasPersonnelViewableAccess,
    hasProfileAccessEditableAccess,
    hasRoleEditableAccess,
    hasSubcontractorPersonnelViewableAccess,
    hasSubordinateManagementFeatureAccess,
    hasTeamDashboardAccess,
    hasTeamEditableAccess,
    hasTeamManagerAccess,
    hasTeamViewableAccess,
    hasTeamPersonnelEditableAccess,
    hasTrainingWriteAccess,
    hasTRPersonnelAccess,
    isAutoEnrolmentIncludingMissingTraining,
    isCourseExpiryDateEditable,
    isMultiDivisionAccount,
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setInitialActiveTabStore(tab);
    if (tab === 'personnel') queryClient.removeQueries({ queryKey: ['teams'] });
  };

  const handleTrainingMatrixButtonClick = () => {
    window.history.pushState(
      {},
      '',
      '/personnel?tab=reports&type=trainingMatrix',
    );
    handleTabClick('reports');
  };

  return (
    <TrainingRegisterResourceManagementContext.Provider
      value={trainingRegisterContextValues}
    >
      <Tabs activeTab={activeTab} onTabClick={handleTabClick}>
        {hasPersonnelViewableAccess && (
          <PersonnelTab
            emailFilter={emailFilter}
            id='personnel'
            initialTrainingFilters={initialTrainingFilters}
            key='personnelTab'
            label={i18nTShared('personnel')}
            onTrainingMatrixButtonClick={handleTrainingMatrixButtonClick}
          />
        )}
        {hasTeamDashboardAccess && (
          <TeamsTab id='teams' key='teamsTab' label={i18nTShared('teams')} />
        )}
        {hasCompanyRolesViewableAccess && (
          <RolesTab id='roles' key='rolesTab' label={i18nTShared('roles')} />
        )}
        {hasCourseViewableAccess && (
          <CoursesTab
            id='courseRegister'
            key='coursesTab'
            label={i18nTShared('courseRegister')}
          />
        )}
        {hasBookingGroupViewableAccess && (
          <BookingsTab
            id='bookings'
            key='bookingsTab'
            label={i18nTShared('bookings')}
          />
        )}
        {hasSubcontractorViewableAccess && (
          <SubcontractorsTab
            id='subcontractors'
            key='subcontractorsTab'
            label={i18nTShared('subcontractors')}
          />
        )}
        {isReportsTabViewable && (
          <TrainingRegisterReportsTab
            id='reports'
            key='trainingRegisterReportsTab'
            label={i18nTShared('reports')}
          />
        )}
      </Tabs>
    </TrainingRegisterResourceManagementContext.Provider>
  );
}

PersonnelIndexView.propTypes = {
  hasPersonnelViewableAccess: PropTypes.bool.isRequired,
  hasPersonnelEditableAccess: PropTypes.bool.isRequired,
  hasTeamViewableAccess: PropTypes.bool.isRequired,
  hasTeamEditableAccess: PropTypes.bool.isRequired,
  hasTeamDashboardAccess: PropTypes.bool.isRequired,
  hasTeamPersonnelEditableAccess: PropTypes.bool.isRequired,
  hasRoleEditableAccess: PropTypes.bool.isRequired,
  hasCourseEditableAccess: PropTypes.bool.isRequired,
  isMultiDivisionAccount: PropTypes.bool.isRequired,
  hasSubcontractorPersonnelViewableAccess: PropTypes.bool.isRequired,
  hasTrainingWriteAccess: PropTypes.bool.isRequired,
  hasArchivedPersonnelAccess: PropTypes.bool.isRequired,
  initialTrainingFilters: PropTypes.object,
  emailFilter: PropTypes.string,
  managerAccessTypes: PropTypes.arrayOf(
    PropTypes.oneOf(['teamManager', 'lineManager']),
  ),
  hasTRPersonnelAccess: PropTypes.bool,
  hasCourseViewableAccess: PropTypes.bool.isRequired,
  hasSubcontractorViewableAccess: PropTypes.bool.isRequired,
  hasCompanyRolesViewableAccess: PropTypes.bool.isRequired,
  hasBookingGroupViewableAccess: PropTypes.bool.isRequired,
  hasProfileAccessEditableAccess: PropTypes.bool.isRequired,
  hasSubordinateManagementFeatureAccess: PropTypes.bool.isRequired,
  isCourseExpiryDateEditable: PropTypes.bool.isRequired,
  hasBookingGroupEditableAccess: PropTypes.bool.isRequired,
  isAutoEnrolmentIncludingMissingTraining: PropTypes.bool.isRequired,
};
